import React, { useState, useRef, useEffect } from 'react';
import { X, Building2, Upload, CheckCircle, AlertCircle, Copy, Check, Tag, Loader2 } from 'lucide-react';
import PaymentService from '../../services/PaymentService';
import { trackBeginCheckout, trackCheckoutSubmitted } from '../../utils/analytics.js';

/**
 * Payment Method Modal — shown when users select a paid plan.
 * Currently only offers Bank Transfer (HBL). Card payment temporarily disabled.
 * Now supports promo code discounts.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onSelectCard: () => void   — legacy no-op (card payments retired)
 * - onSubmitBankTransfer: (receiptFile: File|null, promoCode: string|null) => Promise<void>
 * - planName: string (display name)
 * - planPrice: string (formatted price e.g. "2,999")
 * - planKey: string (plan key e.g. 'select_plus_male')
 * - planCurrency: string ('pkr' or 'usd', default 'pkr')
 * - loading: boolean
 */
const PaymentMethodModal = ({
  isOpen,
  onClose,
  onSelectCard,
  onSubmitBankTransfer,
  planName,
  planPrice,
  planKey,
  planCurrency = 'pkr',
  loading = false,
  disableClose = false,
  userId = null,
}) => {
  const [step, setStep] = useState('bank_details'); // 'bank_details' | 'success'
  const [ocrVerified, setOcrVerified] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const fileInputRef = useRef(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null); // { discount_percent, message, full_discount }
  const [promoError, setPromoError] = useState('');
  const [promoFreeActivated, setPromoFreeActivated] = useState(false);

  const currencyLabel = planCurrency === 'usd' ? '$' : 'PKR ';

  // Checkout intent — fired once per opening of the sheet, in GA4 and Meta.
  const numericPlanPrice = parseFloat(String(planPrice || '').replace(/,/g, ''));
  useEffect(() => {
    if (!isOpen) return;
    trackBeginCheckout({
      plan: planKey || planName,
      value: isNaN(numericPlanPrice) ? null : numericPlanPrice,
      currency: (planCurrency || 'pkr').toUpperCase(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, planKey]);

  // Calculate discounted price
  const getDiscountedPrice = () => {
    if (!promoApplied) return null;
    const numericPrice = parseFloat(planPrice.replace(/,/g, ''));
    if (isNaN(numericPrice)) return null;
    const discounted = numericPrice * (1 - promoApplied.discount_percent / 100);
    return Math.max(0, Math.round(discounted)).toLocaleString();
  };

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    setPromoError('');
    setPromoApplied(null);
    try {
      const res = await PaymentService.validatePromoCode(promoCode.trim(), planKey);
      if (res?.data) {
        setPromoApplied(res.data);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Invalid promo code';
      setPromoError(msg);
    } finally {
      setPromoValidating(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const appliedCode = promoApplied ? promoCode.trim() : null;
      const res = await onSubmitBankTransfer(receiptFile, appliedCode);

      // Check if 100% promo activated the plan directly
      if (res?.data?.data?.promo_applied && res?.data?.data?.discount_percent === 100) {
        setPromoFreeActivated(true);
      }

      setOcrVerified(res?.data?.data?.ocr_verified || false);
      trackCheckoutSubmitted({
        plan: planKey || planName,
        value: isNaN(numericPlanPrice) ? null : numericPlanPrice,
        currency: (planCurrency || 'pkr').toUpperCase(),
      });
      setStep('success');
    } catch (err) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleClose = () => {
    setStep('bank_details');
    setReceiptFile(null);
    setReceiptPreview(null);
    setPromoCode('');
    setPromoApplied(null);
    setPromoError('');
    setPromoFreeActivated(false);
    onClose();
  };

  const discountedPrice = getDiscountedPrice();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#8B1E1E] to-[#5a1212] px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">
              {step === 'success' ? 'Payment Submitted!' : 'Bank Transfer Details'}
            </h3>
            {step === 'bank_details' && (
              <p className="text-white/70 text-sm mt-0.5">{planName} — {currencyLabel}{planPrice}</p>
            )}
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* ── Bank Transfer Details ── */}
          {step === 'bank_details' && (
            <div className="space-y-5">
              {/* Plan & Amount Summary */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Plan</p>
                  <p className="font-bold text-[#222]">{planName}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Amount</p>
                  {promoApplied && discountedPrice ? (
                    <div>
                      <p className="text-sm text-gray-400 line-through">{currencyLabel}{planPrice}</p>
                      <p className="font-bold text-green-700 text-xl">{currencyLabel}{discountedPrice}</p>
                    </div>
                  ) : (
                    <p className="font-bold text-[#222] text-xl">{currencyLabel}{planPrice}</p>
                  )}
                </div>
              </div>

              {/* Promo Code Section */}
              <div className="border border-dashed border-gray-300 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-[#8B1E1E]" />
                  <span className="text-sm font-semibold text-[#222]">Have a promo code?</span>
                </div>

                {promoApplied ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-semibold text-green-800">{promoApplied.discount_percent}% off applied!</p>
                        <p className="text-xs text-green-600">{promoApplied.message}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="text-xs text-red-500 hover:text-red-700 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                      placeholder="Enter code (e.g. SP-ABC123)"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#8B1E1E] focus:border-transparent outline-none"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleValidatePromo(); } }}
                    />
                    <button
                      onClick={handleValidatePromo}
                      disabled={!promoCode.trim() || promoValidating}
                      className="px-4 py-2 bg-[#8B1E1E] text-white rounded-lg text-sm font-semibold hover:bg-[#6d1717] disabled:opacity-50 transition-all flex items-center gap-1.5"
                    >
                      {promoValidating ? (
                        <>
                          <span className="animate-spin w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full" />
                          Checking
                        </>
                      ) : 'Apply'}
                    </button>
                  </div>
                )}

                {promoError && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {promoError}
                  </p>
                )}
              </div>

              {/* HBL Bank Details Card */}
              <div className="border-2 border-[#00765a]/20 rounded-xl overflow-hidden">
                {/* HBL Header */}
                <div className="bg-gradient-to-r from-[#00765a] to-[#004d3a] px-5 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-[#00765a] font-black text-sm">HBL</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-base">Habib Bank Limited</h4>
                    <p className="text-white/70 text-xs">Transfer to the following account</p>
                  </div>
                </div>

                {/* Account Details */}
                <div className="p-5 space-y-3">
                  {[
                    { label: 'Account Title', value: 'SELECT PROPOSAL', copyable: true },
                    { label: 'Account Number', value: '53397000055555', copyable: true },
                    { label: 'IBAN', value: 'PK65HABB0053397000055555', copyable: true },
                    { label: 'Bank', value: 'HBL (Habib Bank Limited)', copyable: false },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-xs text-gray-500">{item.label}</p>
                        <p className="font-semibold text-[#222] text-sm mt-0.5 font-mono">{item.value}</p>
                      </div>
                      {item.copyable && (
                        <button
                          onClick={() => copyToClipboard(item.value, item.label)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                        >
                          {copiedField === item.label ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-green-600" />
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-gray-500">Copy</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ))}

                  {/* User Reference ID */}
                  {userId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mt-2">
                      <p className="text-xs text-blue-700 font-medium">📋 Your Reference ID:</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-lg font-bold text-blue-800 font-mono">SP-{userId}</p>
                        <button
                          onClick={() => copyToClipboard(`SP-${userId}`, 'Reference ID')}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-blue-100"
                        >
                          {copiedField === 'Reference ID' ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-green-600" />
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5 text-blue-400" />
                              <span className="text-blue-500">Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">⚠️ Please enter this as reference / description when transferring the amount</p>
                    </div>
                  )}

                  {/* Show discounted transfer amount prominently */}
                  {promoApplied && discountedPrice && (
                    <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mt-2">
                      <p className="text-xs text-green-700 font-medium">💰 Transfer this amount:</p>
                      <p className="text-lg font-bold text-green-800">{currencyLabel}{discountedPrice}</p>
                      <p className="text-xs text-green-600">({promoApplied.discount_percent}% discount applied)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-600 mb-3">Or scan to pay via HBL app</p>
                <div className="inline-block bg-white rounded-xl shadow-md border border-gray-200 p-3">
                  <img
                    src="/images/hbl-qr.jpg"
                    alt="HBL QR Code — Scan to Pay"
                    className="w-48 h-48 object-contain rounded-lg"
                  />
                </div>
              </div>

              {/* Receipt Upload */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Upload payment receipt for quick activation</p>
                    <p className="text-xs text-amber-700 mt-1">
                      If you don't upload a receipt, manual verification may take up to 24 hours.
                    </p>
                  </div>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  receiptFile
                    ? 'border-green-300 bg-green-50/50'
                    : 'border-gray-300 hover:border-[#8B1E1E] hover:bg-red-50/20'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {receiptPreview ? (
                  <div className="space-y-3">
                    <img
                      src={receiptPreview}
                      alt="Receipt preview"
                      className="max-h-40 mx-auto rounded-lg shadow-sm"
                    />
                    <div className="flex items-center justify-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">{receiptFile.name}</span>
                    </div>
                    <p className="text-xs text-gray-500">Click to change file</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                    <p className="text-sm font-medium text-gray-600">Click to upload receipt screenshot</p>
                    <p className="text-xs text-gray-400">JPG, PNG, or PDF</p>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || (disableClose && !receiptFile)}
                  className="flex-1 py-3 bg-[#8B1E1E] text-white rounded-xl font-semibold text-sm hover:bg-[#6d1717] transition-all disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                      Submitting...
                    </span>
                  ) : receiptFile ? (
                    'Submit with Receipt'
                  ) : disableClose ? (
                    'Upload receipt to continue'
                  ) : (
                    'Submit without Receipt'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ── Success ── */}
          {step === 'success' && (
            <div className="text-center py-4 space-y-4">
              <div className={`w-20 h-20 ${promoFreeActivated || ocrVerified ? 'bg-green-100' : 'bg-amber-50'} rounded-full flex items-center justify-center mx-auto`}>
                <CheckCircle className={`w-10 h-10 ${promoFreeActivated || ocrVerified ? 'text-green-600' : 'text-amber-500'}`} />
              </div>
              <div>
                {promoFreeActivated ? (
                  <>
                    <h3 className="font-bold text-xl text-green-700 mb-2">Plan Activated — 100% Free! 🎉</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Your promo code gave you <strong>100% off</strong>. Your
                      <strong> {planName}</strong> plan is now active instantly!
                    </p>
                  </>
                ) : ocrVerified ? (
                  <>
                    <h3 className="font-bold text-xl text-green-700 mb-2">Receipt Verified — Plan Activated! ✅</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Your receipt was automatically detected and verified. Your
                      <strong> {planName}</strong> plan is now active instantly!
                    </p>
                  </>
                ) : receiptFile ? (
                  <>
                    <h3 className="font-bold text-xl text-[#222] mb-2">Receipt Uploaded!</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We couldn't auto-verify your receipt this time. Our team will manually review it and activate your
                      <strong> {planName}</strong> plan shortly. You'll receive an email once approved.
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      💡 Tip: Uploading a clear, unedited screenshot of your bank receipt enables instant activation.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="font-bold text-xl text-[#222] mb-2">Payment Request Submitted</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Please transfer <strong>{currencyLabel}{promoApplied && discountedPrice ? discountedPrice : planPrice}</strong> to the HBL account shown above.
                      Manual verification may take up to <strong>24 hours</strong>.
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      💡 Tip: Upload your receipt for instant automatic activation!
                    </p>
                  </>
                )}
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-[#8B1E1E] text-white rounded-xl font-semibold text-sm hover:bg-[#6d1717] transition-all"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;
